import React, { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import Button from './Button';
import InputField from './InputField';
import { db } from '../../utils/firebase';
import { getKioskConnectUrl, setClassKioskPin } from '../../utils/kioskAuth';

interface ClassCodeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  classCode: string;
  className?: string;
  grade?: number;
  class?: number;
  schoolId?: string;
  classId?: string;
  scale?: number;
}

const ClassCodeDialog: React.FC<ClassCodeDialogProps> = ({
  isOpen,
  onClose,
  classCode,
  className,
  grade,
  class: classNumber,
  schoolId,
  classId,
  scale = 1
}) => {
  const [copied, setCopied] = useState(false);
  const [kioskUrlCopied, setKioskUrlCopied] = useState(false);
  const [pinSet, setPinSet] = useState(false);
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [pinSaving, setPinSaving] = useState(false);
  const [pinMessage, setPinMessage] = useState<string | null>(null);
  const [pinError, setPinError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !schoolId || !classId) {
      return;
    }

    let cancelled = false;
    const loadPinStatus = async () => {
      try {
        const settingsRef = doc(db, `schools/${schoolId}/classes/${classId}/kioskSettings/default`);
        const snapshot = await getDoc(settingsRef);
        if (!cancelled) {
          setPinSet(Boolean(snapshot.data()?.pinSet));
        }
      } catch (err) {
        console.error('태블릿 PIN 상태 조회 실패:', err);
      }
    };

    void loadPinStatus();
    return () => {
      cancelled = true;
    };
  }, [isOpen, schoolId, classId]);

  // 클립보드에 복사
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(classCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // 2초 후 원래 상태로
    } catch (err) {
      console.error('클립보드 복사 실패:', err);
    }
  };

  const handleCopyKioskUrl = async () => {
    try {
      await navigator.clipboard.writeText(getKioskConnectUrl());
      setKioskUrlCopied(true);
      setTimeout(() => setKioskUrlCopied(false), 2000);
    } catch (err) {
      console.error('태블릿 연동 URL 복사 실패:', err);
    }
  };

  const handleSaveKioskPin = async () => {
    if (!schoolId || !classId) return;
    setPinError(null);
    setPinMessage(null);

    if (!/^\d{4,6}$/.test(newPin.trim())) {
      setPinError('PIN은 4~6자리 숫자입니다.');
      return;
    }
    if (newPin.trim() !== confirmPin.trim()) {
      setPinError('PIN 확인이 일치하지 않습니다.');
      return;
    }

    setPinSaving(true);
    try {
      await setClassKioskPin({ schoolId, classId, pin: newPin.trim() });
      setPinSet(true);
      setNewPin('');
      setConfirmPin('');
      setPinMessage('태블릿 PIN이 저장되었습니다.');
    } catch (err) {
      console.error('태블릿 PIN 저장 실패:', err);
      setPinError('PIN 저장에 실패했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setPinSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'Pretendard',
      zIndex: 1000
    }}>
      <div style={{
        background: '#FFFFFF',
        borderRadius: '16px',
        width: '450px',
        boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.12), 0px 0px 3px rgba(0, 0, 0, 0.06)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        transformOrigin: 'center center',
        transform: `scale(${scale})`,
        position: 'relative'
      }}>
        {/* 메인 컨텐츠 */}
        <div style={{
          width: '394px',
          margin: '24px 28px',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--gap-level-4)'
        }}>
          {/* 상단 영역 */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '40px'
          }}>
            {/* 헤더 */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: '8px'
            }}>
              <div style={{
                flex: '1',
                display: 'flex',
                flexDirection: 'column',
                gap: '2px'
              }}>
                <h1 style={{
                  margin: 0,
                  fontSize: '20px',
                  fontWeight: '600',
                  lineHeight: '30px',
                  color: '#161616',
                  wordWrap: 'break-word',
                  fontFamily: 'Pretendard'
                }}>
                  학급 코드
                </h1>
                <span style={{
                  width: '337px',
                  fontSize: '16px',
                  fontWeight: '500',
                  lineHeight: '24px',
                  color: '#989898',
                  wordWrap: 'break-word',
                  fontFamily: 'Pretendard'
                }}>
                  학급 코드를 사용하여 학생들을 등록할 수 있습니다.
                </span>
              </div>
              
              {/* 닫기 버튼 */}
              <div style={{
                padding: '4px',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onClick={onClose}
              >
                <div style={{
                  width: '24px',
                  height: '24px',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <img 
                    src="/images/icons/closeIcon.svg" 
                    alt="닫기"
                    style={{
                      width: '13.20px',
                      height: '13.20px'
                    }}
                  />
                </div>
              </div>
            </div>

            {/* 학급 코드 카드 */}
            <div style={{
              padding: '16px 20px',
              background: '#F8F8F8',
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px'
            }}>
              <div style={{
                width: '354px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                {/* 제목 */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}>
                  <span style={{
                    fontSize: '16px',
                    fontFamily: 'Pretendard',
                    fontWeight: '600',
                    lineHeight: '24px',
                    wordWrap: 'break-word',
                    display: 'inline'
                  }}>
                    {grade !== undefined && classNumber !== undefined ? (
                      <>
                        <span style={{ color: '#FF6F0F' }}>
                          {grade}학년 {classNumber}반
                        </span>
                        <span style={{ color: '#161616' }}>
                          {' '}학급 코드
                        </span>
                      </>
                    ) : (
                      <>
                        {className && (
                          <span style={{ color: '#FF6F0F' }}>
                            {className}
                          </span>
                        )}
                        <span style={{ color: '#161616' }}>
                          {className ? ' ' : ''}학급 코드
                        </span>
                      </>
                    )}
                  </span>
                </div>

                {/* 코드 영역 */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}>
                  {/* 코드 박스 */}
                  <div style={{
                    padding: '16px 20px',
                    background: '#FFFFFF',
                    borderRadius: '12px',
                    border: '1.2px solid #F0F0F0',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onClick={handleCopy}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#F5F5F5';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#FFFFFF';
                  }}
                  >
                    <span style={{
                      flex: '1',
                      color: '#161616',
                      fontSize: '14px',
                      fontFamily: 'Pretendard',
                      fontWeight: '500',
                      lineHeight: '21px',
                      wordWrap: 'break-word'
                    }}>
                      {classCode}
                    </span>
                    
                    {/* 복사 아이콘 */}
                    <div style={{
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {copied ? (
                        // 복사 완료 체크 아이콘
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M20 6L9 17L4 12" stroke="#FF6F0F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      ) : (
                        // 복사 아이콘
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke="#161616" strokeWidth="1.5" fill="none"/>
                          <path d="M5 15H4C2.89543 15 2 14.1046 2 13V4C2 2.89543 2.89543 2 4 2H13C14.1046 2 15 2.89543 15 4V5" stroke="#161616" strokeWidth="1.5" fill="none"/>
                        </svg>
                      )}
                    </div>
                  </div>

                  {/* 안내 텍스트 */}
                  <span style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    color: '#989898',
                    fontSize: '14px',
                    fontFamily: 'Pretendard',
                    fontWeight: '400',
                    lineHeight: '21px',
                    wordWrap: 'break-word'
                  }}>
                    해당 코드를 학생들에게 공유해주세요.
                  </span>
                </div>
              </div>
            </div>

            {schoolId && classId && (
              <div
                style={{
                  padding: '16px 20px',
                  background: '#F8F8F8',
                  borderRadius: '8px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}
              >
                <div>
                  <div style={{ fontSize: '16px', fontWeight: 600, color: '#161616', lineHeight: '24px' }}>
                    태블릿 연동 PIN
                  </div>
                  <div style={{ fontSize: '14px', color: '#989898', lineHeight: '21px', marginTop: '4px' }}>
                    태블릿에서 학급 코드와 함께 입력합니다. {pinSet ? 'PIN이 설정되어 있습니다.' : 'PIN을 먼저 설정해 주세요.'}
                  </div>
                </div>

                <InputField
                  label="새 PIN"
                  value={newPin}
                  onChange={setNewPin}
                  placeholder="4~6자리 숫자"
                  type="password"
                  size="medium"
                />
                <InputField
                  label="PIN 확인"
                  value={confirmPin}
                  onChange={setConfirmPin}
                  placeholder="다시 입력"
                  type="password"
                  size="medium"
                />

                {pinError && (
                  <div style={{ fontSize: '13px', color: '#C62828' }}>{pinError}</div>
                )}
                {pinMessage && (
                  <div style={{ fontSize: '13px', color: '#2E7D32' }}>{pinMessage}</div>
                )}

                <Button
                  variant="secondary"
                  size="medium"
                  onClick={() => void handleSaveKioskPin()}
                  disabled={pinSaving}
                >
                  {pinSaving ? '저장 중…' : pinSet ? 'PIN 변경' : 'PIN 설정'}
                </Button>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <span style={{ fontSize: '13px', color: '#989898' }}>
                    태블릿에서 아래 주소로 접속한 뒤 학급 코드와 PIN을 입력하세요.
                  </span>
                  <button
                    type="button"
                    onClick={() => void handleCopyKioskUrl()}
                    style={{
                      padding: '12px 14px',
                      borderRadius: '8px',
                      border: '1px solid #F0F0F0',
                      background: 'white',
                      textAlign: 'left',
                      cursor: 'pointer',
                      fontSize: '13px',
                      color: '#161616'
                    }}
                  >
                    {kioskUrlCopied ? '연동 주소가 복사되었습니다.' : getKioskConnectUrl()}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* 완료 버튼 */}
          <Button
            width="100%"
            height="45px"
            variant="submit"
            onClick={onClose}
          >
            완료
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ClassCodeDialog;

